import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Stack,
  Chip,
  Paper,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
  Divider
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';

import civiqLogo from '../img/civiq.jpg';

const Pricing = ({ onNavigate }) => {
  const [isAnnual, setIsAnnual] = useState(false);

  const plans = [
    {
      name: 'Starter',
      icon: <PersonIcon />,
      price: isAnnual ? 999 : 1299,
      originalPrice: isAnnual ? 1299 : null,
      period: isAnnual ? 'year' : 'month',
      description: 'Perfect for individual researchers and students',
      color: '#1976d2',
      popular: false,
      features: [
        'Access to 100 constituencies',
        'Basic demographic data',
        'Historical election results',
        'Standard data exports',
        'Email support',
        'Mobile app access'
      ]
    },
    {
      name: 'Professional',
      icon: <BusinessIcon />,
      price: isAnnual ? 4999 : 6999,
      originalPrice: isAnnual ? 6999 : null,
      period: isAnnual ? 'year' : 'month',
      description: 'Ideal for journalists, analysts, and consultants',
      color: '#7b1fa2',
      popular: true,
      features: [
        'Access to all 650+ constituencies',
        'Advanced demographic analytics',
        'Real-time news integration',
        'AI-powered insights',
        'Custom data exports',
        'Priority email & chat support',
        'API access (5,000 calls/month)',
        'Advanced visualization tools'
      ]
    },
    {
      name: 'Enterprise',
      icon: <GroupsIcon />,
      price: isAnnual ? 19999 : 24999,
      originalPrice: isAnnual ? 24999 : null,
      period: isAnnual ? 'year' : 'month',
      description: 'For political parties, media houses, and organizations',
      color: '#388e3c',
      popular: false,
      features: [
        'Everything in Professional',
        'Unlimited API calls',
        'White-label solutions',
        'Custom integrations',
        'Dedicated account manager',
        'Phone support',
        'Advanced security features',
        'Custom reporting',
        'Team collaboration tools',
        'SLA guarantees'
      ]
    }
  ];

  const savings = isAnnual ? '23% savings' : null;

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)' }}>
        <Toolbar>
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={() => onNavigate('landing')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={civiqLogo}
              alt="CivIQ Logo"
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                mr: 1,
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            />
            {/* <AnalyticsIcon sx={{ mr: 1 }} /> */}
            <Typography variant="h6" fontWeight="bold">
              CivIQ Pricing
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Simple, Transparent
            </Typography>
            <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ 
              background: 'linear-gradient(45deg, #ffd700, #ffeb3b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Pricing
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', mt: 3, mb: 4, opacity: 0.9 }}>
              Choose the perfect plan for your political analytics needs. All plans include our core features.
            </Typography>
            
            {/* Billing Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ opacity: isAnnual ? 0.7 : 1 }}>
                Monthly
              </Typography>
              <FormControlLabel
                control={
                  <Switch 
                    checked={isAnnual} 
                    onChange={(e) => setIsAnnual(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-thumb': {
                        backgroundColor: 'white'
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: alpha('#fff', 0.3)
                      }
                    }}
                  />
                }
                label=""
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ opacity: isAnnual ? 1 : 0.7 }}>
                  Annual
                </Typography>
                {savings && (
                  <Chip 
                    label={savings} 
                    size="small" 
                    sx={{ 
                      backgroundColor: '#4caf50',
                      color: 'white',
                      fontWeight: 600
                    }} 
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Pricing Cards */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ 
                height: '100%',
                position: 'relative',
                transition: 'all 0.3s ease',
                border: plan.popular ? `3px solid ${plan.color}` : '1px solid #e0e0e0',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 20px 40px ${alpha(plan.color, 0.2)}`
                }
              }}>
                {plan.popular && (
                  <Box sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: plan.color,
                    color: 'white',
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    <StarIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Most Popular
                  </Box>
                )}
                
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  {/* Plan Header */}
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    background: alpha(plan.color, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                  }}>
                    {React.cloneElement(plan.icon, { 
                      sx: { color: plan.color, fontSize: 32 } 
                    })}
                  </Box>
                  
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {plan.name}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {plan.description}
                  </Typography>
                  
                  {/* Pricing */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                      <Typography variant="h3" fontWeight="bold" color={plan.color}>
                        ₹{plan.price.toLocaleString()}
                      </Typography>
                      {plan.originalPrice && (
                        <Typography variant="h6" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                          ₹{plan.originalPrice.toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      per {plan.period}
                    </Typography>
                  </Box>
                  
                  {/* CTA Button */}
                  <Button
                    variant={plan.popular ? "contained" : "outlined"}
                    fullWidth
                    size="large"
                    onClick={() => onNavigate('login')}
                    sx={{
                      mb: 3,
                      py: 1.5,
                      fontWeight: 600,
                      backgroundColor: plan.popular ? plan.color : 'transparent',
                      borderColor: plan.color,
                      color: plan.popular ? 'white' : plan.color,
                      '&:hover': {
                        backgroundColor: plan.popular ? plan.color : alpha(plan.color, 0.1),
                        borderColor: plan.color
                      }
                    }}
                  >
                    {plan.popular ? 'Start Free Trial' : 'Get Started'}
                  </Button>
                  
                  {/* Features */}
                  <List sx={{ textAlign: 'left' }}>
                    {plan.features.map((feature, featureIndex) => (
                      <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon sx={{ color: plan.color, fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.secondary'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ Section */}
      <Box sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            Frequently Asked Questions
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Got questions? We've got answers.
          </Typography>
          
          <Stack spacing={3}>
            {[
              {
                question: "Is there a free trial available?",
                answer: "Yes! We offer a 14-day free trial for all plans. No credit card required to start."
              },
              {
                question: "Can I change plans anytime?",
                answer: "Absolutely. You can upgrade or downgrade your plan at any time. Changes take effect immediately."
              },
              {
                question: "What payment methods do you accept?",
                answer: "We accept all major credit cards, debit cards, UPI, and net banking for Indian customers."
              },
              {
                question: "Do you offer discounts for educational institutions?",
                answer: "Yes, we provide special educational discounts for universities and research institutions. Contact us for details."
              },
              {
                question: "Is my data secure?",
                answer: "We use enterprise-grade security with SSL encryption and comply with Indian data protection laws."
              }
            ].map((faq, index) => (
              <Paper key={index} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  {faq.question}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {faq.answer}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)', color: 'white' }}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Ready to Start?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join thousands of political analysts already using CivIQ
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button 
                variant="contained" 
                size="large"
                onClick={() => onNavigate('login')}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: alpha('#fff', 0.9)
                  }
                }}
              >
                Start Free Trial
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                onClick={() => onNavigate('contact')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: alpha('#fff', 0.1)
                  }
                }}
              >
                Contact Sales
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
      {/* Footer */}
            <Box sx={{ py: 6, backgroundColor: '#263238', color: 'white' }}>
              <Container maxWidth="lg">
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          // background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2
                        }}
                      >
                        {/* <AnalyticsIcon sx={{ color: 'white', fontSize: 24 }} /> */}
                        <Box
                                      component="img"
                                      src={civiqLogo}
                                      alt="CivIQ Logo"
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 1,
                                        mr: 1,
                                        border: '1px solid rgba(255,255,255,0.3)'
                                      }}
                                    />
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        CivIQ
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ maxWidth: 400 }}>
                      AI-powered political analytics platform for India's elections. Making complex political data accessible and actionable.
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Platform
                        </Typography>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('features')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            Features
                          </Button>
                        </Box>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('pricing')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            Pricing
                          </Button>
                        </Box>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('api')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            API
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Company
                        </Typography>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('about')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            About
                          </Button>
                        </Box>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('contact')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            Contact
                          </Button>
                        </Box>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('privacy')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            Privacy Policy
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.2)' }} />
                
                <Box textAlign="center">
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    © 2025 CivIQ. All rights reserved.
                  </Typography>
                </Box>
              </Container>
            </Box>
    </Box>
  );
};

export default Pricing;
